import { useState } from 'react';
import { detectPlatform } from '@tanstack/hotkeys';
import { useHotkey } from '@tanstack/react-hotkeys';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  Command as CommandKey,
  FileText,
  ListOrdered,
  Search,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils.ts';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog.tsx';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command.tsx';
import Toggle from '@/components/ui/Toggle.tsx';
import useGlobalSearchState from '@/features/dashboard/globalSearch/hooks/useGlobalSearchState.ts';
import {
  BookingHit,
  GlobalSearchEmpty,
  GlobalSearchResultsSkeleton,
  PersonHit,
  TourHit,
} from '@/features/dashboard/globalSearch/components/GlobalSearchHit.tsx';
import { ParticipantStatus } from '@/features/dashboard/participants/types/ParticipantStatus.ts';
import { ClientStatus } from '@/features/dashboard/clients/types/ClientStatus.ts';
import {
  GLOBAL_SEARCH_GROUPS,
  INCLUDE_CANCELLED_LABEL,
  OPEN_LIST_LABEL,
  listLink,
  waitlistHitLink,
} from '@/features/dashboard/globalSearch/globalSearchGroups.ts';

const SEARCH_HOTKEY = 'Mod+K';
/** Klawisz ⌘ to ikona, nie znak: znak `⌘` renderuje się różnie na każdej platformie. */
const IS_MAC = detectPlatform() === 'mac';

const itemClass =
  'gap-3 rounded-lg px-2.5 py-2 text-sm text-base-text data-[selected=true]:bg-accent-100 data-[selected=true]:text-base-text';
const groupClass =
  'px-2 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.09em] [&_[cmdk-group-heading]]:text-neutral-500';

/**
 * Wyszukiwarka szerokiego ekranu: pole w sidebarze (albo ⌘K) otwiera okno,
 * w którym jedna fraza przeszukuje sekcje aplikacji oraz wyjazdy, uczestników,
 * zapisujących, rezerwacje i listę oczekujących.
 */
const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const {
    term,
    setTerm,
    includeCancelled,
    setIncludeCancelled,
    sections,
    results,
    waiting,
    noResults,
    reset,
  } = useGlobalSearchState();

  useHotkey(SEARCH_HOTKEY, () => setOpen((current) => !current));

  const close = () => {
    setOpen(false);
    reset();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-[34px] w-full items-center gap-2 rounded-[10px] border border-neutral-200 bg-neutral-50 px-2.5 text-left text-sm text-neutral-500 transition-colors hover:border-neutral-300"
      >
        <Search aria-hidden className="size-4 shrink-0 text-neutral-500" />
        <span className="flex-1 truncate">Szukaj…</span>
        <kbd className="flex items-center gap-px rounded-[5px] border border-neutral-200 bg-basic-white px-1.5 text-[11px] font-semibold text-neutral-500">
          {IS_MAC ? (
            <CommandKey aria-label="Cmd" className="size-3" />
          ) : (
            'Ctrl+'
          )}
          K
        </kbd>
      </button>

      <Dialog
        open={open}
        onOpenChange={(next) => (next ? setOpen(true) : close())}
      >
        <DialogContent
          aria-describedby={undefined}
          className="top-[16%] max-w-[560px] translate-y-0 overflow-hidden p-0 shadow-preview"
        >
          <Command shouldFilter={false} className="rounded-xl">
            <CommandInput
              value={term}
              onValueChange={setTerm}
              placeholder="Szukaj sekcji, wyjazdu, osoby, rezerwacji…"
              className="h-12 text-[15px] text-base-text placeholder:text-neutral-400"
            />
            {/* Enter z przełącznika nie może dojść do cmdk, który wybrałby podświetlony wynik zamiast przełączyć. */}
            <div
              className="flex justify-end border-b border-neutral-100 px-4 py-2"
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.stopPropagation();
              }}
            >
              <Toggle
                size="sm"
                label={INCLUDE_CANCELLED_LABEL}
                checked={includeCancelled}
                onCheckedChange={setIncludeCancelled}
              />
            </div>
            <CommandList className="max-h-[420px] py-2">
              {noResults && <GlobalSearchEmpty term={term} />}

              {sections.length > 0 && (
                <CommandGroup heading="Sekcje" className={groupClass}>
                  {sections.map((item) => (
                    <CommandItem
                      key={item.label}
                      value={`section:${item.label}`}
                      onSelect={() => {
                        close();
                        void navigate({ to: item.to });
                      }}
                      className={itemClass}
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-accent-100 text-accent-700">
                        <item.icon aria-hidden size={17} />
                      </span>
                      <span className="font-semibold">{item.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {waiting && <GlobalSearchResultsSkeleton />}

              {results &&
                GLOBAL_SEARCH_GROUPS.filter(
                  (group) => results[group.key].length > 0,
                ).map((group) => (
                  <CommandGroup
                    key={group.key}
                    heading={group.label}
                    className={groupClass}
                  >
                    {group.key === 'tours' &&
                      results.tours.map((hit) => (
                        <CommandItem
                          key={hit.tour.id}
                          value={`tour:${hit.tour.id}`}
                          onSelect={() => {
                            close();
                            void navigate({
                              to: '/app/tours/$tourId',
                              params: { tourId: hit.tour.id },
                            });
                          }}
                          className={itemClass}
                        >
                          <TourHit hit={hit} />
                        </CommandItem>
                      ))}
                    {group.key === 'participants' &&
                      results.participants.map((hit) => (
                        <CommandItem
                          key={hit.participant.id}
                          value={`participant:${hit.participant.id}`}
                          onSelect={() => {
                            close();
                            void navigate({
                              to: '/app/participants/$participantId',
                              params: { participantId: hit.participant.id },
                            });
                          }}
                          className={itemClass}
                        >
                          <PersonHit
                            person={hit.participant}
                            tourName={hit.tour?.name}
                            icon={Users}
                            cancelled={
                              hit.participant.status ===
                              ParticipantStatus.WITHDRAWN
                            }
                          />
                        </CommandItem>
                      ))}
                    {group.key === 'clients' &&
                      results.clients.map((hit) => (
                        <CommandItem
                          key={hit.client.id}
                          value={`client:${hit.client.id}`}
                          onSelect={() => {
                            close();
                            void navigate({
                              to: '/app/clients/$clientId',
                              params: { clientId: hit.client.id },
                            });
                          }}
                          className={itemClass}
                        >
                          <PersonHit
                            person={hit.client}
                            tourName={hit.tour?.name}
                            icon={FileText}
                            cancelled={
                              hit.client.status === ClientStatus.CANCELLED
                            }
                          />
                        </CommandItem>
                      ))}
                    {group.key === 'bookings' &&
                      results.bookings.map((hit) => (
                        <CommandItem
                          key={hit.booking.id}
                          value={`booking:${hit.booking.id}`}
                          onSelect={() => {
                            close();
                            void navigate({
                              to: '/app/bookings/$bookingId',
                              params: { bookingId: hit.booking.id },
                            });
                          }}
                          className={itemClass}
                        >
                          <BookingHit hit={hit} />
                        </CommandItem>
                      ))}
                    {group.key === 'waitlist' &&
                      results.waitlist.map((hit) => (
                        <CommandItem
                          key={hit.client.id}
                          value={`waitlist:${hit.client.id}`}
                          onSelect={() => {
                            close();
                            void navigate(waitlistHitLink(hit));
                          }}
                          className={itemClass}
                        >
                          <PersonHit
                            person={hit.client}
                            tourName={hit.tour?.name}
                            icon={ListOrdered}
                          />
                        </CommandItem>
                      ))}
                    {/* Ostatnia pozycja grupy, nie link w nagłówku: cmdk chodzi strzałkami tylko po pozycjach. */}
                    <CommandItem
                      value={`${group.key}:list`}
                      onSelect={() => {
                        close();
                        void navigate(listLink(group, term, includeCancelled));
                      }}
                      className={cn(itemClass, 'text-neutral-500')}
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center">
                        <ArrowRight aria-hidden size={16} />
                      </span>
                      <span className="text-xs font-semibold">
                        {OPEN_LIST_LABEL}
                      </span>
                    </CommandItem>
                  </CommandGroup>
                ))}
            </CommandList>
          </Command>
          {/*
           * `DialogTitle` dokleja własny „X”; okno zamyka Escape i klik poza nim.
           * Stoi za treścią, żeby autofokus okna trafił w pole, nie w ukryty „X”.
           */}
          <div className="sr-only">
            <DialogTitle>Wyszukiwarka</DialogTitle>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GlobalSearch;
